import type { ISbLink } from 'storyblok-js-client'
import { useCache } from './useCache'

interface LinkData {
  id: string
  path: string
  name: string
  parentId: string | null
}

const fetchAllSettings = async (settingsPath: string): Promise<LinkData[]> => {
  try {
    // For some reason, the nuxt instance becomes unavailable inside the useAsyncData, so we have to wrap
    // it in runWithContext.
    const result = await useAsyncData(settingsPath, () => useNuxtApp().runWithContext(() => {
      const settings = useStoryblokSettings()
      return useCache(generateStoryblokLinksCacheKey(settings.accessToken, settingsPath), async() => {
        const links = await useStoryblokApi().getAll('cdn/links', {
          ...settings.apiSettings,
          starts_with: settingsPath,
        }) as ISbLink[]
        const data = links
          .filter((link): link is ISbLink & Required<Pick<ISbLink, 'id' | 'slug'>> => link.id !== undefined && link.slug !== undefined)
          .map((link) => ({
            id: `${link.id}`,
            path: link.slug,
            // name property of ISbLink is just the display name, the system name is build in into the slug
            name: link.slug.split('/').pop()!,
            parentId: link.parent_id ? `${link.parent_id}` : null,
          }))
        return data
      })
    }))
    if (result.error.value) {
      throw result.error.value
    }
    return result.data.value ?? []
  } catch (error) {
    throw new Error('Fetching storyblok settings links failed.', { cause: error })
  }
}

export const useSettingsPath = async () => {
  const { storyblok, relativePath } = await usePageConfig()

  const trees = buildTrees(await fetchAllSettings(storyblok.settingsPath))
  const parent = findClosestNode(trees, join(storyblok.settingsPath, relativePath))
  if (parent !== null) {
    return {
      headerPath: findNodeAlongParents(parent, 'header')?.path ?? null,
      footerPath: findNodeAlongParents(parent, 'footer')?.path ?? null,
    }
  }

  return {
    headerPath: null,
    footerPath: null,
  }
}
